import React, { useMemo } from 'react'
import { Alert } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'

import { AttendeesInput } from '@/components/Event/AttendeesInput'
import { useRegisterStepFields } from '@/components/Event/CrForm/useStepField'
import { Card } from '@/components/Event/Detail/components/Card'
import { useEventZoomParticipants } from '@/components/Event/NewScript/queries/scriptData'
import { AttendeeRole } from '@/constants'
import { Event, ReporterFeedback } from '@/types'
import {
  getAttendeeRolesForEvent,
  isWitnessLocationRequired,
} from '@/utils/parrot'
import { getEventTimezone } from '@/utils/time'

const ATTENDEE_FIELDS = [
  'name',
  'email',
  'role',
  'present',
  'zoom_participants',
  // Witness-only and state-dependent; the validators no-op elsewhere.
  'city',
  'state',
] as const

// The "Attendees present" box. Extracted from FeedbackWitnessForm so the same markup
// can be rendered inside the staged CR Form's Attendance step without
// duplication — it binds to the shared form and renders identically wherever it mounts.
export const WitnessAttendees: React.FC<{
  event: Event
  form: UseFormReturnType<ReporterFeedback>
  witnessIndex: number
  crLayout?: boolean
  onAddWitness?: () => void
  addWitnessDisabled?: boolean
}> = ({
  event,
  form,
  witnessIndex,
  crLayout = false,
  onAddWitness,
  addWitnessDisabled,
}) => {
  const attendeeRoles = useMemo(() => getAttendeeRolesForEvent(event), [event])

  // The Zoom-name mapping only renders in the staged CR layout, so only fetch there.
  const { data: zoomParticipants } = useEventZoomParticipants(event, {
    enabled: crLayout,
  })

  // Register the attendee field paths with the enclosing step (if any) so the step's
  // checkbox can validate them. Keyed on attendee count so it doesn't churn per change.
  // zoom_coverage is registered too, so clicking the step's status circle validates
  // participant coverage — not just the per-attendee fields.
  const attendeeCount =
    form.values.events_feedbacks?.[witnessIndex]?.attendees?.length ?? 0
  const attendeeFieldPaths = useMemo(() => {
    const paths = Array.from({ length: attendeeCount }, (_, i) =>
      ATTENDEE_FIELDS.map(
        field => `events_feedbacks.${witnessIndex}.attendees.${i}.${field}`,
      ),
    ).flat()
    if (crLayout) {
      paths.push(`events_feedbacks.${witnessIndex}.zoom_coverage`)
      paths.push(`events_feedbacks.${witnessIndex}.witness_role`)
    }
    return paths
  }, [attendeeCount, witnessIndex, crLayout])
  useRegisterStepFields(attendeeFieldPaths)

  // Coverage feedback appears once the SA tries to finish attendance — the zoom_coverage
  // error is set by validating the step (status circle) or submitting; it is NOT shown
  // proactively. The unassigned list is recomputed live from the current mappings, so it
  // updates as the SA assigns participants and disappears once none remain — no stale,
  // already-fixed error lingering.
  const attendees =
    form.values.events_feedbacks?.[witnessIndex]?.attendees ?? []
  const mappedParticipantIds = new Set(
    attendees.flatMap(attendee =>
      (attendee.zoom_participants ?? []).map(zp => zp.id),
    ),
  )
  const unassignedParticipants = (zoomParticipants ?? []).filter(
    participant => !mappedParticipantIds.has(participant.id),
  )
  const showCoverageError =
    crLayout &&
    !!form.errors[`events_feedbacks.${witnessIndex}.zoom_coverage`] &&
    unassignedParticipants.length > 0

  // Exactly one Witness required (mirrors DR). Same after-attempt + live pattern as
  // coverage: shown once validated, recomputed live so it clears when the roles are fixed.
  const witnessCount = attendees.filter(
    attendee => attendee.role === AttendeeRole.WITNESS,
  ).length
  const showWitnessError =
    crLayout &&
    !!form.errors[`events_feedbacks.${witnessIndex}.witness_role`] &&
    witnessCount !== 1

  return (
    <Card heading="Attendees present">
      {showWitnessError && (
        <Alert color="red" mb="md" data-invalid="true">
          There must be exactly one witness (found {witnessCount}).
        </Alert>
      )}
      {showCoverageError && (
        <Alert color="red" mb="md" data-invalid="true">
          Assign every Zoom participant to an attendee. Unassigned:{' '}
          {unassignedParticipants.map(p => p.user_name).join(', ')}
        </Alert>
      )}
      <AttendeesInput
        form={form}
        roles={attendeeRoles}
        witnessIndex={witnessIndex}
        crLayout={crLayout}
        witnessLocationEnabled={isWitnessLocationRequired(event)}
        zoomParticipants={zoomParticipants}
        timezone={getEventTimezone(event)}
        onAddWitness={onAddWitness}
        addWitnessDisabled={addWitnessDisabled}
      />
    </Card>
  )
}
